<template>
  <v-card class="admin-game-card">
    <v-card-title class="justify-center">
      <span style="color: #e53935;">Red</span>Chirp Snake
      <v-spacer></v-spacer>
      <v-btn icon @click="closeGame">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="text-center">
      <!-- Game Over Message -->
      <div v-if="gameOver" class="game-over-message">
        <h2>Game Over!</h2>
        <h3>Your final score: {{ score }}</h3>
        <v-btn color="primary" class="mt-4" @click="startGame">Play Again</v-btn>
      </div>

      <!-- Game Instructions -->
      <div v-if="!gameActive && !gameOver" class="game-instructions">
        <p>Use arrow keys to control the <span style="color: #e53935;">red bird</span>.</p>
        <p>Don't hit the walls or yourself!</p>
        <v-btn color="success" class="mt-4" @click="startGame">Start Game</v-btn>
      </div>

      <!-- Game Board -->
      <div class="game-container">
        <div class="game-stats" v-if="gameActive">
          <v-chip color="primary" class="ma-2">Score: {{ score }}</v-chip>
        </div>

        <!-- Control buttons for mobile or as fallback -->
        <div class="control-buttons" v-if="gameActive">
          <v-btn icon @click="changeDirection('up')" :disabled="direction === 'down'">
            <v-icon>mdi-arrow-up</v-icon>
          </v-btn>
          <div class="horizontal-controls">
            <v-btn icon @click="changeDirection('left')" :disabled="direction === 'right'">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-btn icon @click="changeDirection('down')" :disabled="direction === 'up'">
              <v-icon>mdi-arrow-down</v-icon>
            </v-btn>
            <v-btn icon @click="changeDirection('right')" :disabled="direction === 'left'">
              <v-icon>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="game-board" :class="{ active: gameActive, 'game-over': gameOver }" tabindex="0" ref="gameBoard">
          <div
            v-for="(row, rowIndex) in board"
            :key="'row-' + rowIndex"
            class="board-row"
          >
            <div
              v-for="(cell, colIndex) in row"
              :key="'cell-' + rowIndex + '-' + colIndex"
              class="board-cell"
              :class="{
                'snake-cell': isSnakeCell(rowIndex, colIndex) && !isSnakeHead(rowIndex, colIndex),
                'snake-head': isSnakeHead(rowIndex, colIndex),
                'food-cell': isFoodCell(rowIndex, colIndex)
              }"
            >
              <!-- Snake Head (Bird Icon) -->
              <v-icon v-if="isSnakeHead(rowIndex, colIndex)" class="head-icon" :style="getHeadRotationStyle()">
                mdi-bird
              </v-icon>

              <!-- Food (Message Icon) -->
              <v-icon v-if="isFoodCell(rowIndex, colIndex)" class="food-icon">
                {{ getRandomMessageIcon() }}
              </v-icon>
            </div>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  data() {
    return {
      // Game state
      gameActive: false,
      gameOver: false,
      score: 0,

      // Game board
      boardSize: 20,
      board: [],

      // Snake
      snake: [],
      direction: 'right',
      nextDirection: 'right',

      // Food
      food: { x: 0, y: 0 },

      // Game loop
      gameInterval: null,
      gameSpeed: 150, // milliseconds between moves

      // Icons
      messageIcons: ['mdi-message', 'mdi-message-text', 'mdi-message-outline'],
      currentFoodIcon: 'mdi-message'
    }
  },

  created() {
    // Initialize the board
    this.initializeBoard();
  },

  mounted() {
    // We'll add the keyboard event listener when the game starts
  },

  beforeDestroy() {
    // Make sure to clean up
    this.removeKeyboardListener();
    this.stopGameLoop();
  },

  methods: {
    initializeBoard() {
      // Create a 2D array for the board
      this.board = Array(this.boardSize).fill().map(() => Array(this.boardSize).fill(0));
    },

    startGame() {
      // Reset game state
      this.gameActive = true;
      this.gameOver = false;
      this.score = 0;

      // Initialize snake in the middle of the board
      const middle = Math.floor(this.boardSize / 2);
      this.snake = [
        { x: middle, y: middle },
        { x: middle - 1, y: middle },
        { x: middle - 2, y: middle }
      ];

      // Set initial direction
      this.direction = 'right';
      this.nextDirection = 'right';

      // Generate initial food
      this.generateFood();

      // Add keyboard listener
      this.addKeyboardListener();

      // Start game loop
      this.startGameLoop();
    },

    addKeyboardListener() {
      // Add keyboard event listener to the game board element
      if (this.$refs.gameBoard) {
        this.$refs.gameBoard.addEventListener('keydown', this.handleKeyDown);
        // Focus the game board to capture keyboard events
        this.$refs.gameBoard.focus();
      }
    },

    removeKeyboardListener() {
      // Remove keyboard event listener from the game board element
      if (this.$refs.gameBoard) {
        this.$refs.gameBoard.removeEventListener('keydown', this.handleKeyDown);
      }
    },

    stopGameLoop() {
      if (this.gameInterval) {
        clearInterval(this.gameInterval);
        this.gameInterval = null;
      }
    },

    startGameLoop() {
      this.stopGameLoop();
      this.gameInterval = setInterval(() => {
        this.moveSnake();
      }, this.gameSpeed);
    },

    handleKeyDown(event) {
      if (!this.gameActive) return;

      // Prevent default behavior for arrow keys to avoid scrolling the page
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault();
      }

      // Handle arrow keys
      switch (event.key) {
        case 'ArrowUp':
          this.changeDirection('up');
          break;
        case 'ArrowDown':
          this.changeDirection('down');
          break;
        case 'ArrowLeft':
          this.changeDirection('left');
          break;
        case 'ArrowRight':
          this.changeDirection('right');
          break;
      }
    },

    changeDirection(newDirection) {
      // Prevent 180-degree turns (which would cause immediate game over)
      if (
        (newDirection === 'up' && this.direction !== 'down') ||
        (newDirection === 'down' && this.direction !== 'up') ||
        (newDirection === 'left' && this.direction !== 'right') ||
        (newDirection === 'right' && this.direction !== 'left')
      ) {
        this.nextDirection = newDirection;
      }
    },

    moveSnake() {
      // Update direction
      this.direction = this.nextDirection;

      // Get current head position
      const head = { ...this.snake[0] };

      // Calculate new head position based on direction
      switch (this.direction) {
        case 'up':
          head.y--;
          break;
        case 'down':
          head.y++;
          break;
        case 'left':
          head.x--;
          break;
        case 'right':
          head.x++;
          break;
      }

      // Check for collision with walls
      if (head.x < 0 || head.x >= this.boardSize || head.y < 0 || head.y >= this.boardSize) {
        this.endGame();
        return;
      }

      // Check for collision with self
      for (let i = 0; i < this.snake.length; i++) {
        if (this.snake[i].x === head.x && this.snake[i].y === head.y) {
          this.endGame();
          return;
        }
      }

      // Add new head to snake
      this.snake.unshift(head);

      // Check if snake ate food
      if (head.x === this.food.x && head.y === this.food.y) {
        // Increase score
        this.score++;

        // Generate new food
        this.generateFood();

        // Play sound
        try {
          this.$rc.playSound();
        } catch (e) {
          // Ignore errors
        }
      } else {
        // Remove tail if no food was eaten
        this.snake.pop();
      }
    },

    generateFood() {
      // Generate random position for food
      let x, y;
      let validPosition = false;

      while (!validPosition) {
        x = Math.floor(Math.random() * this.boardSize);
        y = Math.floor(Math.random() * this.boardSize);

        // Check if position is not occupied by snake
        validPosition = true;
        for (let i = 0; i < this.snake.length; i++) {
          if (this.snake[i].x === x && this.snake[i].y === y) {
            validPosition = false;
            break;
          }
        }
      }

      this.food = { x, y };

      // Select a random message icon for the food
      const randomIndex = Math.floor(Math.random() * this.messageIcons.length);
      this.currentFoodIcon = this.messageIcons[randomIndex];
    },

    endGame() {
      this.gameActive = false;
      this.gameOver = true;
      this.stopGameLoop();
      this.removeKeyboardListener();
    },

    closeGame() {
      this.gameActive = false;
      this.gameOver = false;
      this.stopGameLoop();
      this.removeKeyboardListener();
      this.$emit('close');
    },

    isSnakeCell(row, col) {
      return this.snake.some(segment => segment.y === row && segment.x === col);
    },

    isSnakeHead(row, col) {
      return this.snake.length > 0 && this.snake[0].y === row && this.snake[0].x === col;
    },

    isFoodCell(row, col) {
      return this.food.y === row && this.food.x === col;
    },

    getRandomMessageIcon() {
      // Return the current food icon
      return this.currentFoodIcon;
    },

    getHeadRotationStyle() {
      // Rotate the bird icon based on the direction of movement
      let rotation = 0;
      switch (this.direction) {
        case 'up':
          rotation = 270;
          break;
        case 'down':
          rotation = 90;
          break;
        case 'left':
          rotation = 180;
          break;
        case 'right':
          rotation = 0;
          break;
      }
      return { transform: `rotate(${rotation}deg)` };
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-game-card {
  max-width: 600px;
  margin: 0 auto;
}

.game-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
}

.game-board {
  display: grid;
  grid-template-rows: repeat(20, 1fr);
  width: 400px;
  height: 400px;
  border: 2px solid #333;
  background-color: #f5f5f5;
  margin: 0 auto;
  outline: none; /* Remove default focus outline */
}

.game-board:focus {
  border-color: #e53935; /* Red highlight border when focused */
  box-shadow: 0 0 8px rgba(229, 57, 53, 0.6);
}

.board-row {
  display: grid;
  grid-template-columns: repeat(20, 1fr);
}

.board-cell {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.snake-cell {
  background-color: #e53935; /* Red color for snake body */
  border-radius: 2px;
}

.snake-head {
  display: flex;
  justify-content: center;
  align-items: center;
}

.head-icon {
  color: #e53935; /* Red color for bird head */
  font-size: 20px !important;
}

.food-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.food-icon {
  color: #2196F3; /* Blue color for message icons */
  font-size: 18px !important;
}

.game-stats {
  margin-bottom: 15px;
}

.game-over-message {
  padding: 20px;
  margin-bottom: 20px;
}

.game-over-message h2 {
  font-size: 28px;
  margin-bottom: 10px;
  color: #e53935; /* Keep the Game Over text red */
}

.game-over-message h3 {
  font-size: 22px;
  margin-bottom: 20px;
}

.game-instructions {
  padding: 20px;
  margin-bottom: 20px;
}

.game-instructions p {
  margin-bottom: 10px;
  font-size: 16px;
}

.control-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 15px;
}

.horizontal-controls {
  display: flex;
  justify-content: center;
  margin-top: 5px;
}
</style>
